import { getApiBaseUrl } from './api';
import { logout } from './authService';

/**
 * Onboarding event types from the validx agent (via proxy).
 *
 * The proxy translates validx events:
 *   session_id → { type: 'session_id', sessionId }
 *   text       → { type: 'message', content }
 *   tool_event → { type: 'tool_event', e, d }
 *   usage      → { type: 'usage', ... }
 *   done       → { type: 'complete' }
 *   error      → { type: 'error', message }
 */
export interface OnboardingEvent {
  type: 'session_id' | 'message' | 'tool_event' | 'tool_result' | 'usage' | 'complete' | 'error';
  // session_id
  sessionId?: string;
  // message (text delta)
  content?: string;
  // tool_event sub-events
  e?: string;
  d?: any;
  // tool_result (connectApp widget data from validx)
  toolName?: string;
  result?: any;
  toolCallId?: string;
  // usage
  inputTokens?: number;
  outputTokens?: number;
  costUsd?: number;
  // error
  message?: string;
}

/** Tool event subtypes (the `e` field inside tool_event) */
export type ToolEventType =
  | 'task:create' | 'task:update'
  | 'client'
  | 'complete';

/**
 * Optional framing flags for the onboarding stream. These are purely
 * prompt-shaping — the backend uses them to tell the agent which entry path
 * the user is on (so it doesn't demand a URL from a no-website user, for
 * instance) and which surface (onboarding build vs My Business dashboard).
 */
export interface StreamOnboardingOptions {
  lane?: 'website' | 'upload' | 'connect' | 'dashboard';
  surface?: 'onboarding' | 'dashboard' | 'knowledge';
}

/**
 * SSE onboarding stream. Posts to the onboarding endpoint and reads SSE events.
 * Returns an abort function.
 */
export function streamOnboarding(
  message: string,
  history: { role: string; content: string }[],
  sessionId: string | null,
  completedActions: string[] | undefined,
  onEvent: (event: OnboardingEvent) => void,
  onError: (error: string) => void,
  onComplete: () => void,
  options?: StreamOnboardingOptions,
): () => void {
  const controller = new AbortController();
  const { signal } = controller;

  const apiUrl = `${getApiBaseUrl()}/onboarding/stream`;
  const token = localStorage.getItem('auth_token');

  fetch(apiUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'text/event-stream',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      message,
      history,
      ...(sessionId ? { session_id: sessionId } : {}),
      ...(completedActions && completedActions.length > 0 ? { completed_actions: completedActions } : {}),
      ...(options?.lane ? { lane: options.lane } : {}),
      ...(options?.surface ? { surface: options.surface } : {}),
    }),
    signal,
  })
    .then(async (response) => {
      if (!response.ok) {
        // Handle expired JWT — clear credentials and redirect to login
        if (response.status === 401 || response.status === 403) {
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user_id');
          localStorage.removeItem('user_role');
          logout('/login');
          return;
        }
        const text = await response.text();
        onError(`HTTP ${response.status}: ${text}`);
        return;
      }

      const reader = response.body?.getReader();
      if (!reader) {
        onError('Could not get response reader');
        return;
      }

      const decoder = new TextDecoder();
      let buffer = '';
      let completed = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6)) as OnboardingEvent;
              if (data.type === 'complete') {
                completed = true;
                onComplete();
              } else if (data.type === 'error') {
                completed = true;
                onError(data.message || 'Unknown error');
              } else {
                onEvent(data);
              }
            } catch {
              // Ignore malformed JSON
            }
          }
        }
      }

      if (!completed) {
        onComplete();
      }
    })
    .catch((err) => {
      if (err.name !== 'AbortError') {
        onError(err.message || 'Stream failed');
      }
    });

  return () => controller.abort();
}

/**
 * Playbook-completeness endpoint response shape (aibot /onboarding/playbook-completeness).
 *
 * One score, one list of missing fields ranked by weight. The UI and the chat
 * both read this to drive the user toward 100% completeness.
 */
export interface PlaybookCompleteness {
  score: number;
  status: 'red' | 'yellow' | 'green';
  filled_count: number;
  total_count: number;
  filled: Array<{ path: string; label: string; weight: number }>;
  missing: Array<{ path: string; label: string; weight: number }>;
  top_gap: null | {
    path: string;
    label: string;
    weight: number;
    estimated_gain: number;
  };
}

/**
 * Recent activity feed event — what the My Business page renders as a
 * reverse-chronological changelog.
 */
export interface ActivityEvent {
  id: string;
  kind: string;
  actor: 'user' | 'orbit' | string;
  title: string;
  occurred_at: string | null;
  metadata?: Record<string, any>;
}

/**
 * Knowledge base summary — what Orbit has indexed about the business.
 * Reads exclusively from `kb_documents` (the new canonical store).
 */
export interface KnowledgeBaseSummary {
  client_id: string;
  totals: {
    pages: number;
    documents: number;
    videos: number;
    products: number;
    other: number;
    all: number;
  };
  by_source: Array<{
    label: string;
    type: string;
    count: number;
    last_added: string | null;
    subtitle: string | null;
  }>;
  recent: Array<{
    id: string;
    source_type: string;
    source_url: string | null;
    title: string;
    summary: string | null;
    created_at: string | null;
  }>;
}

/**
 * KB document inside a single source bucket — one row from kb_documents.
 */
export interface KbDocument {
  id: string;
  source_type: string;
  source_url: string | null;
  title: string;
  summary: string | null;
  embed_status: string | null;
  created_at: string | null;
}

export async function fetchSourceDocuments(opts: {
  sourceId: string;
  clientId?: string;
  page?: number;
  limit?: number;
}): Promise<{ source_id: string; page: number; limit: number; total: number; has_more: boolean; documents: KbDocument[] }> {
  const token = localStorage.getItem('auth_token');
  const qs = new URLSearchParams();
  if (opts.clientId) qs.set('client_id', opts.clientId);
  if (opts.page) qs.set('page', String(opts.page));
  if (opts.limit) qs.set('limit', String(opts.limit));
  const res = await fetch(
    `${getApiBaseUrl()}/onboarding/knowledge-base/source/${encodeURIComponent(opts.sourceId)}/documents?${qs.toString()}`,
    { headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) } },
  );
  if (!res.ok) throw new Error(`source documents ${res.status}`);
  return res.json();
}

export async function addKbUrl(opts: { url: string; clientId?: string }): Promise<{ kb_document_id: string; title: string; url: string; embed_status: string }> {
  const token = localStorage.getItem('auth_token');
  const res = await fetch(`${getApiBaseUrl()}/onboarding/knowledge-base/sources/url`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ url: opts.url, ...(opts.clientId ? { client_id: opts.clientId } : {}) }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.detail || body?.error || `add-url ${res.status}`);
  }
  return res.json();
}

export async function startKbCrawl(opts: { url: string; clientId?: string }): Promise<{ crawl_id: string; url: string; status: string }> {
  const token = localStorage.getItem('auth_token');
  const res = await fetch(`${getApiBaseUrl()}/onboarding/knowledge-base/sources/crawl`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ url: opts.url, ...(opts.clientId ? { client_id: opts.clientId } : {}) }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.detail || body?.error || `crawl ${res.status}`);
  }
  return res.json();
}

export async function uploadKbDocument(opts: { file: File; clientId?: string }): Promise<{ kb_document_id: string; title: string; filename: string }> {
  const token = localStorage.getItem('auth_token');
  const fd = new FormData();
  fd.append('file', opts.file);
  if (opts.clientId) fd.append('client_id', opts.clientId);
  const res = await fetch(`${getApiBaseUrl()}/onboarding/knowledge-base/sources/upload`, {
    method: 'POST',
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: fd,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.detail || body?.error || `upload ${res.status}`);
  }
  return res.json();
}

export async function refreshKbSource(opts: { sourceId: string; clientId?: string }): Promise<{ crawl_id: string; status: string }> {
  const token = localStorage.getItem('auth_token');
  const res = await fetch(
    `${getApiBaseUrl()}/onboarding/knowledge-base/source/${encodeURIComponent(opts.sourceId)}/refresh`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(opts.clientId ? { client_id: opts.clientId } : {}),
    },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.detail || body?.error || `refresh ${res.status}`);
  }
  return res.json();
}

export async function deleteKbSource(opts: { sourceId: string; clientId?: string }): Promise<{ source_id: string; deleted_documents: number }> {
  const token = localStorage.getItem('auth_token');
  const qs = new URLSearchParams();
  if (opts.clientId) qs.set('client_id', opts.clientId);
  const res = await fetch(
    `${getApiBaseUrl()}/onboarding/knowledge-base/source/${encodeURIComponent(opts.sourceId)}?${qs.toString()}`,
    {
      method: 'DELETE',
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.detail || body?.error || `delete ${res.status}`);
  }
  return res.json();
}

export async function fetchKnowledgeBase(opts: {
  clientId?: string;
  recentLimit?: number;
} = {}): Promise<KnowledgeBaseSummary> {
  const token = localStorage.getItem('auth_token');
  const qs = new URLSearchParams();
  if (opts.clientId) qs.set('client_id', opts.clientId);
  if (opts.recentLimit) qs.set('recent_limit', String(opts.recentLimit));
  const res = await fetch(`${getApiBaseUrl()}/onboarding/knowledge-base?${qs.toString()}`, {
    headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  if (!res.ok) throw new Error(`knowledge-base ${res.status}`);
  return res.json();
}

export async function fetchRecentActivity(opts: {
  clientId?: string;
  limit?: number;
} = {}): Promise<{ events: ActivityEvent[] }> {
  const token = localStorage.getItem('auth_token');
  const qs = new URLSearchParams();
  if (opts.clientId) qs.set('client_id', opts.clientId);
  if (opts.limit) qs.set('limit', String(opts.limit));
  const res = await fetch(`${getApiBaseUrl()}/onboarding/activity?${qs.toString()}`, {
    headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  if (!res.ok) throw new Error(`activity ${res.status}`);
  return res.json();
}

export async function fetchPlaybookCompleteness(clientId?: string): Promise<PlaybookCompleteness> {
  const token = localStorage.getItem('auth_token');
  const qs = clientId ? `?client_id=${encodeURIComponent(clientId)}` : '';
  const res = await fetch(`${getApiBaseUrl()}/onboarding/playbook-completeness${qs}`, {
    headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  if (!res.ok) throw new Error(`playbook-completeness ${res.status}`);
  return res.json();
}

/**
 * Direct-authorize response from validx (proxied via aibot).
 *
 * Bypasses the onboarding LLM stream — the Connect lane uses this so the
 * OAuth popup gets a redirect URL in ~1–2s instead of ~7–15s.
 */
export interface InitiateAuthResponse {
  redirectUrl: string;
  composioClientId: string;
  connectionId: string;
  alreadyConnected: boolean;
}

/**
 * Patch a single business_intelligence section.
 *
 * Drives the inline-edit affordance on the My Business page. For object
 * sections (company, brand, contact, audience, …) the patch is a partial
 * dict that gets shallow-merged. For array sections (offerings, team,
 * tech_stack, social_presence, competitors) the patch is appended unless
 * `replace: true` is passed.
 */
export async function patchIntelligenceField(opts: {
  section: string;
  patch: any;
  clientId?: string;
  replace?: boolean;
}): Promise<{ value?: any } & Record<string, any>> {
  const token = localStorage.getItem('auth_token');
  const res = await fetch(`${getApiBaseUrl()}/onboarding/intelligence-field`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      section: opts.section,
      patch: opts.patch,
      ...(opts.clientId ? { client_id: opts.clientId } : {}),
      ...(opts.replace ? { replace: true } : {}),
    }),
  });
  if (!res.ok) {
    let detail = `intelligence-field ${res.status}`;
    try {
      const body = await res.json();
      detail = body?.detail || body?.error || detail;
    } catch { /* keep default */ }
    throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
  }
  return res.json();
}

/**
 * Welcome-page disambiguation: ask the backend to find the most likely
 * homepage for a business name the user typed. Returns the top homepage
 * candidate so the UI can confirm "Found {Business} at {URL} — is this you?"
 * before routing the user to the research lane.
 *
 * Returns { found: false } if Firecrawl returned nothing usable. The UI
 * should fall back to the connect lane in that case.
 */
export interface SearchBusinessCandidate {
  url: string;
  host: string;
  title: string;
  description?: string;
}

export async function searchBusiness(opts: {
  query: string;
  limit?: number;
}): Promise<{ found: boolean; top?: SearchBusinessCandidate; candidates?: SearchBusinessCandidate[]; error?: string }> {
  const res = await fetch(`${getApiBaseUrl()}/onboarding/search-business`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({ query: opts.query, limit: opts.limit ?? 5 }),
  });
  if (!res.ok) {
    return { found: false, error: `search-business ${res.status}` };
  }
  return res.json();
}

/**
 * Per-item add / update / remove on an array section of business_intelligence.
 * Powers Pass 4 — inline edit + add + delete on the matrix tiles
 * (OfferingsMatrix, CompetitorsMatrix, etc).
 *
 *   add    → append `item`
 *   update → replace entry at `index` with `item`
 *   remove → drop entry at `index`
 *
 * `section` must be one of {offerings, team, tech_stack, social_presence,
 * competitors}. Returns the new array on success.
 */
export async function patchIntelligenceArrayItem(opts: {
  section: string;
  op: 'add' | 'update' | 'remove';
  item?: any;
  index?: number;
  clientId?: string;
}): Promise<{ value?: any[] } & Record<string, any>> {
  const token = localStorage.getItem('auth_token');
  const res = await fetch(`${getApiBaseUrl()}/onboarding/intelligence-field/array`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      section: opts.section,
      op: opts.op,
      ...(opts.item !== undefined ? { item: opts.item } : {}),
      ...(typeof opts.index === 'number' ? { index: opts.index } : {}),
      ...(opts.clientId ? { client_id: opts.clientId } : {}),
    }),
  });
  if (!res.ok) {
    let detail = `intelligence-field/array ${res.status}`;
    try {
      const body = await res.json();
      detail = body?.detail || body?.error || detail;
    } catch { /* keep default */ }
    throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
  }
  return res.json();
}

/**
 * Revoke (disconnect) a Composio OAuth connection. After this returns
 * successfully, the connection is gone — the user has to OAuth again to
 * reconnect. The Connected Apps section calls this from the manage popover's
 * Disconnect button.
 */
export async function revokeConnection(opts: {
  provider: string;
  clientId?: string;
}): Promise<{ revoked: boolean; accountId?: string; error?: string }> {
  const token = localStorage.getItem('auth_token');
  const res = await fetch(`${getApiBaseUrl()}/onboarding/revoke-connection`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      provider: opts.provider,
      ...(opts.clientId ? { client_id: opts.clientId } : {}),
    }),
  });
  if (!res.ok) {
    let detail = `revoke-connection ${res.status}`;
    try {
      const body = await res.json();
      detail = body?.detail || body?.error || detail;
    } catch { /* keep default */ }
    throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
  }
  return res.json();
}

/**
 * Check connection status for a provider. Returns `{ connected, provider, toolkit }`.
 * Used by the Connected Apps section to poll for OAuth completion after the
 * user finishes (or cancels) the popup flow.
 */
export async function getConnectionStatus(opts: {
  provider: string;
  composioClientId?: string;
  clientId?: string;
}): Promise<{ connected?: boolean; active?: boolean; status?: string; provider?: string; toolkit?: string }> {
  const token = localStorage.getItem('auth_token');
  const qs = new URLSearchParams({ provider: opts.provider });
  if (opts.composioClientId) qs.set('composioClientId', opts.composioClientId);
  if (opts.clientId) qs.set('clientId', opts.clientId);
  const res = await fetch(`${getApiBaseUrl()}/agents/planner/connection-status?${qs.toString()}`, {
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  if (!res.ok) throw new Error(`connection-status ${res.status}`);
  return res.json();
}

/** Composio connection row — powers My Business → Connected Apps. */
export interface OnboardingConnectionRow {
  id: string;
  provider: string;
  status: string;
  last_sync_at?: string | null;
  error?: string | null;
}

/** List OAuth connections for a client (Composio-backed). */
export async function fetchConnections(clientId?: string): Promise<OnboardingConnectionRow[]> {
  const token = localStorage.getItem('auth_token');
  const qs = clientId ? `?client_id=${encodeURIComponent(clientId)}` : '';
  const res = await fetch(`${getApiBaseUrl()}/onboarding/connections${qs}`, {
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  if (!res.ok) throw new Error(`connections ${res.status}`);
  const data = await res.json();
  return Array.isArray(data?.connections) ? data.connections : [];
}

export async function initiateOnboardingAuth(opts: {
  provider: string;
  clientId?: string;
  sessionId?: string | null;
}): Promise<InitiateAuthResponse> {
  const token = localStorage.getItem('auth_token');
  const res = await fetch(`${getApiBaseUrl()}/onboarding/initiate-auth`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      provider: opts.provider,
      ...(opts.clientId ? { client_id: opts.clientId } : {}),
      ...(opts.sessionId ? { session_id: opts.sessionId } : {}),
    }),
  });
  if (!res.ok) {
    let detail = `initiate-auth ${res.status}`;
    try {
      const body = await res.json();
      if (body?.detail) detail = typeof body.detail === 'string' ? body.detail : JSON.stringify(body.detail);
      else if (body?.error) detail = body.error;
    } catch { /* keep default */ }
    throw new Error(detail);
  }
  return res.json();
}
